/**
 * App 根组件 —— 提供内容加载、HashRouter 布局与全局 Loading/Error 状态。
 */

import { HashRouter, Routes, Route, Link, NavLink } from 'react-router-dom';
import { useRepository } from '@/hooks/useRepository';
import { useLibraryStore } from '@/store/useLibraryStore';

import { Search, BookHeart, History, Home } from 'lucide-react';
import { HomePage } from '@/features/home/HomePage';
import { ModulePage } from '@/features/module/ModulePage';
import { TopicPage } from '@/features/task/TopicPage';
import { SearchPage } from '@/features/search/SearchPage';
import { FavoritesPage } from '@/features/home/FavoritesPage';
import { RecentPage } from '@/features/home/RecentPage';

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-sage border-t-transparent" />
        <p className="text-sm text-slate-500">加载星露谷百科…</p>
      </div>
    </div>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  return (
    <div className="flex h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">
        <p className="mb-2 text-lg font-semibold text-red-600">加载失败</p>
        <p className="mb-4 text-sm text-slate-500">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-sage px-4 py-2 text-sm text-white hover:bg-sage/80"
        >
          重试
        </button>
      </div>
    </div>
  );
}

function Header() {
  const favoritesCount = useLibraryStore((s) => s.favoriteIDs.length);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-canvas-light/95 backdrop-blur-sm dark:border-slate-700/40 dark:bg-canvas-dark/95">
      <div className="mx-auto flex h-14 max-w-content items-center justify-between px-4">
        <NavLink to="/" className="flex items-center gap-2 text-lg font-bold text-sage">
          <Home size={22} />
          <span>星露谷助手</span>
        </NavLink>

        <nav className="flex items-center gap-3">
          <Link
            to="/search"
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <Search size={16} />
            <span className="hidden sm:inline">搜索</span>
          </Link>

          <Link
            to="/favorites"
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <BookHeart size={16} />
            {favoritesCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-sage px-1 text-xs text-white">
                {favoritesCount}
              </span>
            )}
          </Link>

          <Link
            to="/recent"
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <History size={16} />
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function App() {
  const { repository, loading, error } = useRepository();

  if (loading) return <LoadingScreen />;
  if (error || !repository) return <ErrorScreen error={error ?? new Error('未知错误')} />;

  return (
    <HashRouter>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="mx-auto w-full max-w-content flex-1 px-4 py-6">
          <Routes>
            <Route path="/" element={<HomePage repository={repository} />} />
            <Route path="/module/:moduleId" element={<ModulePage repository={repository} />} />
            <Route path="/topic/:topicId" element={<TopicPage repository={repository} />} />
            <Route path="/search" element={<SearchPage repository={repository} />} />
            <Route path="/favorites" element={<FavoritesPage repository={repository} />} />
            <Route path="/recent" element={<RecentPage repository={repository} />} />
          </Routes>
        </main>
      </div>
    </HashRouter>
  );
}
